/**
 * O botão que **age na tela** pergunta o que a ação exige — issue #408, segunda onda.
 *
 * O defeito: cancelar e concluir um agendamento eram botões crus na tela de detalhe, ao lado
 * de um `RouteActionButton` que perguntava certo, enquanto a mesma ação aparecia guardada na
 * tela de lista. As duas telas se contradiziam, e quem abrisse pelo detalhe clicava para
 * receber uma recusa. `RouteActionButton` não alcançava esses controles porque eles não levam
 * a lugar nenhum: a ação acontece ali mesmo, e não há rota de destino para perguntar por.
 *
 * Aqui a pergunta é a mesma — permissão, vínculo, perspectiva e módulo —, só que sobre a
 * permissão da ação e sobre a tela onde o controle vive, que `useAcao` lê da rota atual.
 *
 * Nunca some: bloqueado, o botão continua à vista, desabilitado, com o motivo no tooltip.
 */

import React from 'react';
import { Button, Tooltip, type ButtonProps } from '@mui/material';

import { useAcao } from '../../authorization/useVeredito';
import type { Modulo } from '../../types/manifesto';

export interface ActionButtonProps extends Omit<ButtonProps, 'href'> {
  /** A permissão que o servidor exige da ação — a mesma do `pode()` da rota de API. */
  acao: string | readonly string[];
  /**
   * Como a ação se chama. Vai para o tooltip e para o nome acessível, e é o que o leitor de
   * tela anuncia mesmo com o botão bloqueado.
   */
  rotulo: string;
  /**
   * O módulo comercial que cobre a ação, quando não é o da tela — o caso da aba de eSocial,
   * que mora na ficha da empresa.
   */
  modulo?: Modulo;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  acao,
  rotulo,
  modulo,
  disabled,
  children,
  ...props
}) => {
  const controle = useAcao(acao, rotulo, { modulo });

  if (!controle.disabled) {
    return (
      <Button {...props} aria-label={props['aria-label'] ?? rotulo} disabled={disabled}>
        {children}
      </Button>
    );
  }

  // O `<span>` existe porque botão desabilitado não emite evento de mouse, e sem ele o
  // tooltip — que aqui é a única explicação que a pessoa recebe — nunca abriria.
  return (
    <Tooltip title={controle.tooltip}>
      <span>
        <Button {...props} aria-label={props['aria-label'] ?? rotulo} disabled onClick={undefined}>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ActionButton;
